import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Dashboard.css';
import { tableAPI } from '../services/api';

const Dashboard = () => {
  const [tableData, setTableData] = useState({
    table1: [],
    table2: [],
    table3: [],
    table4: [],
    table5: [],
    table6: [],
    table7_moneyin: [],
    table7_moneyout: []
  });
  
  const [activeTab, setActiveTab] = useState('INR'); // 'INR', 'AED', 'Source', 'Client', 'Money Tracker'
  const [activeClientSubTab, setActiveClientSubTab] = useState('profit'); // 'profit' or 'amount'
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    // Load data from MySQL with localStorage fallback
    const loadData = async () => {
      const loadTableData = async (tableId) => {
        try {
          const mysqlData = await tableAPI.getById(tableId);
          if (mysqlData && mysqlData.length > 0) {
            localStorage.setItem(`table_${tableId}`, JSON.stringify(mysqlData));
            return mysqlData;
          }
          if (mysqlData && Array.isArray(mysqlData) && mysqlData.length === 0) {
            localStorage.removeItem(`table_${tableId}`);
            return [];
          }
        } catch (e) {
          console.error(`Error fetching table_${tableId} from MySQL:`, e);
        }
        try {
          const saved = localStorage.getItem(`table_${tableId}`);
          if (saved) {
            return JSON.parse(saved);
          }
        } catch (e) {
          console.error(`Error loading table_${tableId} from localStorage:`, e);
        }
        return [];
      };

      let table1Data = await loadTableData('table1');
      
      // Check if data exists but only has one year (need to regenerate with 5 years)
      let needsRegeneration = false;
      if (table1Data && table1Data.length > 1) {
        const years = new Set();
        const dateIndex = table1Data[0].indexOf('Date');
        if (dateIndex >= 0) {
          table1Data.slice(1).forEach(row => {
            if (row && row[dateIndex]) {
              const dateStr = row[dateIndex];
              const parts = dateStr.split('/');
              if (parts.length === 3) {
                years.add(parseInt(parts[2], 10));
              }
            }
          });
          // If only one year or less than 2 years, regenerate
          if (years.size < 2) {
            needsRegeneration = true;
          }
        }
      }
      

      let table2Data = await loadTableData('table2');
      
      // Check if data exists but only has one year (need to regenerate with 5 years)
      let needsRegeneration2 = false;
      if (table2Data && table2Data.length > 1) {
        const years = new Set();
        const dateIndex = table2Data[0].indexOf('Date');
        if (dateIndex >= 0) {
          table2Data.slice(1).forEach(row => {
            if (row && row[dateIndex]) {
              const dateStr = row[dateIndex];
              const parts = dateStr.split('/');
              if (parts.length === 3) {
                years.add(parseInt(parts[2], 10));
              }
            }
          });
          // If only one year or less than 2 years, regenerate
          if (years.size < 2) {
            needsRegeneration2 = true;
          }
        }
      }
      
      
      // Parse table1 data (array format: [header, ...rows])
      const parsedTable1 = [];
      if (table1Data && table1Data.length > 1) {
        const headers = table1Data[0];
        const dateIndex = headers.indexOf('Date');
        const codeIndex = headers.indexOf('Code');
        const purposeIndex = headers.indexOf('Purpose');
        const currencyIndex = headers.indexOf('Currency');
        const amountIndex = headers.indexOf('Amount');
        const crossProfitIndex = headers.indexOf('Cross Profit');
        const expenseIndex = headers.indexOf('Expense');
        const netProfitIndex = headers.indexOf('Net-profit');

        table1Data.slice(1).forEach(row => {
          if (row && row.length > 0) {
            parsedTable1.push({
              date: row[dateIndex] || '',
              code: row[codeIndex] || '',
              purpose: row[purposeIndex] || '',
              currency: row[currencyIndex] || '',
              amount: parseFloat(row[amountIndex]) || 0,
              crossProfit: parseFloat(row[crossProfitIndex]) || 0,
              expense: parseFloat(row[expenseIndex]) || 0,
              netProfit: parseFloat(row[netProfitIndex]) || 0,
            });
          }
        });
      }

      // Parse table2 data (Expense List)
      const parsedTable2 = [];
      if (table2Data && table2Data.length > 1) {
        const headers = table2Data[0];
        // Case-insensitive header lookup - check for both "Purpose" and "Category"
        const dateIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'date');
        const purposeIndex = headers.findIndex(h => {
          const hLower = String(h || '').toLowerCase();
          return hLower === 'purpose' || hLower === 'category';
        });
        const currencyIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'currency');
        const amountIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'amount');

        table2Data.slice(1).forEach(row => {
          if (row && row.length > 0) {
            parsedTable2.push({
              date: dateIndex !== -1 ? (row[dateIndex] || '') : '',
              purpose: purposeIndex !== -1 ? (row[purposeIndex] || '') : '',
              currency: currencyIndex !== -1 ? (row[currencyIndex] || '') : '',
              amount: amountIndex !== -1 ? (parseFloat(row[amountIndex]) || 0) : 0,
            });
          }
        });
      }

      // Load and parse table3 data (Stock Updates)
      let table3Data = await loadTableData('table3');
      const parsedTable3 = [];
      if (table3Data && table3Data.length > 1) {
        const headers = table3Data[0];
        const nameIndex = headers.indexOf('Name');
        const codeIndex = headers.indexOf('Source');
        const amountIndex = headers.indexOf('Amount');
        const dateIndex = headers.indexOf('Date');

        table3Data.slice(1).forEach(row => {
          if (row && row.length > 0) {
            parsedTable3.push({
              name: row[nameIndex] || '',
              code: row[codeIndex] || '',
              amount: parseFloat(row[amountIndex]) || 0,
              date: dateIndex !== -1 ? (row[dateIndex] || '') : ''
            });
          }
        });
      }

      // Load and parse table5 data (Source and Client) FIRST - needed for table4 name lookup
      let table5Data = await loadTableData('table5');
      const parsedTable5 = [];
      if (table5Data && table5Data.length > 1) {
        const headers = table5Data[0];
        const nameIndex = headers.indexOf('Name');
        const typeIndex = headers.indexOf('Type');
        const codeIndex = headers.indexOf('Code');

        table5Data.slice(1).forEach(row => {
          if (row && row.length > 0) {
            parsedTable5.push({
              name: row[nameIndex] || '',
              type: row[typeIndex] || '',
              code: row[codeIndex] || ''
            });
          }
        });
      }

      // Load and parse table4 data (Transactions)
      let table4Data = await loadTableData('table4');
      
      // Check if data exists but only has one year (need to regenerate with 5 years)
      let needsRegeneration4 = false;
      if (table4Data && table4Data.length > 1) {
        const years = new Set();
        const dateIndex = table4Data[0].indexOf('Date');
        if (dateIndex >= 0) {
          table4Data.slice(1).forEach(row => {
            if (row && row[dateIndex]) {
              const dateStr = row[dateIndex];
              const parts = dateStr.split('/');
              if (parts.length === 3) {
                years.add(parseInt(parts[2], 10));
              }
            }
          });
          // If only one year or less than 2 years, regenerate
          if (years.size < 2) {
            needsRegeneration4 = true;
          }
        }
      }
      
      // Create code-to-name mapping from table5
      const codeToNameMap = {};
      if (table5Data && table5Data.length > 1) {
        const headers = table5Data[0];
        const codeIndex = headers.indexOf('Code');
        const nameIndex = headers.indexOf('Name');
        if (codeIndex !== -1 && nameIndex !== -1) {
          table5Data.slice(1).forEach(row => {
            if (row && row[codeIndex] && row[nameIndex]) {
              const code = String(row[codeIndex]).trim();
              const name = String(row[nameIndex]).trim();
              if (code && name) {
                codeToNameMap[code] = name;
              }
            }
          });
        }
      }
      
      const parsedTable4 = [];
      if (table4Data && table4Data.length > 1) {
        const headers = table4Data[0];
        const codeIndex = headers.indexOf('Code');
        const dateIndex = headers.indexOf('Date');
        const aedIndex = headers.indexOf('AED');
        const inrIndex = headers.indexOf('INR');
        const netProfitIndex = headers.indexOf('Net Profit');

        table4Data.slice(1).forEach(row => {
          if (row && row.length > 0) {
            const code = row[codeIndex] || '';
            parsedTable4.push({
              name: codeToNameMap[code] || code || 'Unknown', // Lookup name from table5 using code
              code: code,
              date: row[dateIndex] || '',
              aed: aedIndex !== -1 ? (parseFloat(row[aedIndex]) || 0) : 0,
              inr: inrIndex !== -1 ? (parseFloat(row[inrIndex]) || 0) : 0,
              inrAmount: inrIndex !== -1 ? (parseFloat(row[inrIndex]) || 0) : 0, // Keep backward compatibility
              netProfit: parseFloat(row[netProfitIndex]) || 0,
            });
          }
        });
      }

      // Load and parse table6 data (Stock Balance)
      let table6Data = await loadTableData('table6');
      const parsedTable6 = [];
      if (table6Data && table6Data.length > 1) {
        const headers = table6Data[0];
        const rateIndex = headers.findIndex(header => header && header.toLowerCase().includes('buying rate'));
        const amountIndex = headers.indexOf('Total Amount');
        const currencyIndex = headers.indexOf('Currency');
        // Last Updated column removed

        table6Data.slice(1).forEach(row => {
          if (row && row.length > 0) {
            parsedTable6.push({
              buyingRate: rateIndex !== -1 ? row[rateIndex] : '',
              totalAmount: amountIndex !== -1 ? parseFloat(row[amountIndex]) || 0 : 0,
              currency: currencyIndex !== -1 ? row[currencyIndex] || '' : ''
            });
          }
        });
      }


      // Load Money Tracker data (Money In and Money Out)
      let table7MoneyInData = await loadTableData('table7_moneyin');
      let table7MoneyOutData = await loadTableData('table7_moneyout');
      
      const parsedTable7MoneyIn = [];
      if (table7MoneyInData && table7MoneyInData.length > 1) {
        const headers = table7MoneyInData[0];
        const nameIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'name');
        const codeIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'code');
        const receivedDateIndex = headers.findIndex(h => String(h || '').toLowerCase().includes('received date'));
        const receivedAmtIndex = headers.findIndex(h => String(h || '').toLowerCase().includes('received amt'));
        const chargesIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'charges');
        const totalReturnableIndex = headers.findIndex(h => String(h || '').toLowerCase().includes('total returnable'));
        const balanceIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'balance');
        const remarksIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'remarks');
        
        table7MoneyInData.slice(1).forEach(row => {
          if (row && row.length > 0) {
            parsedTable7MoneyIn.push({
              name: nameIndex !== -1 ? (row[nameIndex] || '') : '',
              code: codeIndex !== -1 ? (row[codeIndex] || '') : '',
              receivedDate: receivedDateIndex !== -1 ? (row[receivedDateIndex] || '') : '',
              receivedAmt: receivedAmtIndex !== -1 ? (parseFloat(row[receivedAmtIndex]) || 0) : 0,
              charges: chargesIndex !== -1 ? (parseFloat(row[chargesIndex]) || 0) : 0,
              totalReturnable: totalReturnableIndex !== -1 ? (parseFloat(row[totalReturnableIndex]) || 0) : 0,
              balance: balanceIndex !== -1 ? (parseFloat(row[balanceIndex]) || 0) : 0,
              remarks: remarksIndex !== -1 ? (row[remarksIndex] || '') : ''
            });
          }
        });
      }
      
      const parsedTable7MoneyOut = [];
      if (table7MoneyOutData && table7MoneyOutData.length > 1) {
        const headers = table7MoneyOutData[0];
        const nameIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'name');
        const codeIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'code');
        const receivedDateIndex = headers.findIndex(h => String(h || '').toLowerCase().includes('received date'));
        const receivedAmtIndex = headers.findIndex(h => String(h || '').toLowerCase().includes('received amt'));
        const chargesIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'charges');
        const totalReturnableIndex = headers.findIndex(h => String(h || '').toLowerCase().includes('total returnable'));
        const balanceIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'balance');
        const remarksIndex = headers.findIndex(h => String(h || '').toLowerCase() === 'remarks');
        
        table7MoneyOutData.slice(1).forEach(row => {
          if (row && row.length > 0) {
            parsedTable7MoneyOut.push({
              name: nameIndex !== -1 ? (row[nameIndex] || '') : '',
              code: codeIndex !== -1 ? (row[codeIndex] || '') : '',
              receivedDate: receivedDateIndex !== -1 ? (row[receivedDateIndex] || '') : '',
              receivedAmt: receivedAmtIndex !== -1 ? (parseFloat(row[receivedAmtIndex]) || 0) : 0,
              charges: chargesIndex !== -1 ? (parseFloat(row[chargesIndex]) || 0) : 0,
              totalReturnable: totalReturnableIndex !== -1 ? (parseFloat(row[totalReturnableIndex]) || 0) : 0,
              balance: balanceIndex !== -1 ? (parseFloat(row[balanceIndex]) || 0) : 0,
              remarks: remarksIndex !== -1 ? (row[remarksIndex] || '') : ''
            });
          }
        });
      }

      setTableData({
        table1: parsedTable1,
        table2: parsedTable2,
        table3: parsedTable3,
        table4: parsedTable4,
        table5: parsedTable5,
        table6: parsedTable6,
        table7_moneyin: parsedTable7MoneyIn,
        table7_moneyout: parsedTable7MoneyOut
      });
    };
    
    loadData();
    // DISABLED: Don't reload every second - data should come from MySQL, not localStorage
    // const interval = setInterval(loadData, 1000);
    // return () => clearInterval(interval);
  }, []);

  // Parse date from dd/mm/yyyy format
  const parseDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    const parts = dateString.trim().split('/');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    return { day, month, year };
  };

  // Get current date info
  const getCurrentDateInfo = () => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1
    };
  };

  // Get last month date info
  const getLastMonthDateInfo = () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      year: lastMonth.getFullYear(),
      month: lastMonth.getMonth() + 1
    };
  };

  // Get last year date info
  const getLastYearDateInfo = () => {
    const now = new Date();
    return {
      year: now.getFullYear() - 1,
      month: now.getMonth() + 1
    };
  };

  // Filter data by period
  const filterByPeriod = (data, period) => {
    if (period === 'all') {
      return data;
    }
    
    let targetYear, targetMonth;
    if (period === 'lastMonth') {
      const lastMonth = getLastMonthDateInfo();
      targetYear = lastMonth.year;
      targetMonth = lastMonth.month;
    } else if (period === 'lastYear') {
      const lastYear = getLastYearDateInfo();
      targetYear = lastYear.year;
      targetMonth = null; // All months of last year
    } else if (period === 'current') {
      const current = getCurrentDateInfo();
      targetYear = current.year;
      targetMonth = null; // All months of current year
    } else if (period === 'currentMonth') {
      const current = getCurrentDateInfo();
      targetYear = current.year;
      targetMonth = current.month;
      } else {
      const current = getCurrentDateInfo();
      targetYear = current.year;
      targetMonth = current.month;
    }

    return data.filter(item => {
      const dateInfo = parseDate(item.date);
      if (!dateInfo) return false;
      
      if (period === 'lastYear' || period === 'current') {
        return dateInfo.year === targetYear;
      }
      
      return dateInfo.year === targetYear && dateInfo.month === targetMonth;
    });
  };

  // Calculate overall profit for a period, optionally filtered by currency
  const calculateOverallProfit = (period = 'all', currencyFilter = null) => {
    const filteredTable1 = filterByPeriod(tableData.table1, period);
    const filteredTable2 = filterByPeriod(tableData.table2, period);
    const filteredTable4 = filterByPeriod(tableData.table4, period);

    // Filter by currency if specified
    const currencyFilteredTable1 = currencyFilter 
      ? filteredTable1.filter(item => String(item.currency || '').toUpperCase().trim() === currencyFilter)
      : filteredTable1;
    const currencyFilteredTable2 = currencyFilter 
      ? filteredTable2.filter(item => String(item.currency || '').toUpperCase().trim() === currencyFilter)
      : filteredTable2;
    
    // For transactions (table4), filter by currency if specified
    // Transactions have separate AED and INR columns, not a currency column
    let currencyFilteredTable4 = filteredTable4;
    if (currencyFilter) {
      currencyFilteredTable4 = filteredTable4.filter(item => {
        const currency = String(currencyFilter).toUpperCase().trim();
        if (currency === 'AED') {
          // Transactions are always INR; never include them when AED is selected
          return false;
        } else if (currency === 'INR') {
          // Only include transactions with INR > 0 (using inr field, fallback to inrAmount for backward compatibility)
          return ((item.inr || item.inrAmount || 0) > 0);
        }
        // For other currencies, don't include transactions
        return false;
      });
    }

    const a1TotalNetProfit = currencyFilteredTable1.reduce((sum, item) => {
      return sum + (item.netProfit || 0);
    }, 0);

    const transactionsTotalNetProfit = currencyFilteredTable4.reduce((sum, item) => {
      if (currencyFilter === 'INR' && ((item.inr || item.inrAmount || 0) <= 0)) {
        return sum;
      }
      return sum + (item.netProfit || 0);
    }, 0);

    const expenseTotalAmount = currencyFilteredTable2.reduce((sum, item) => {
      return sum + (item.amount || 0);
    }, 0);

    const overallProfit = a1TotalNetProfit + transactionsTotalNetProfit - expenseTotalAmount;

    return {
      a1TotalNetProfit,
      transactionsTotalNetProfit,
      expenseTotalAmount,
      overallProfit,
      currency: currencyFilter
    };
  };

  // Calculate overall balance for a period, optionally filtered by currency
  // Overall Balance = Overall Profit + Stock Balance (always add, never subtract)
  // Where Overall Profit = A1 total net profit + Transaction total net profit - total expenses
  const calculateOverallBalance = (period = 'all', currencyFilter = null) => {
    const profitData = calculateOverallProfit(period, currencyFilter);
    
    // Stock balance is current (not time-based), always include it
    // Filter stock balance by currency if currency filter is specified
    // When currencyFilter is 'AED', include all stock balance entries with currency 'AED'
    // When currencyFilter is 'INR', include all stock balance entries with currency 'INR'
    const stockBalanceTotalAmount = tableData.table6.reduce((sum, item) => {
      if (!currencyFilter) {
        // No currency filter - include all stock balance regardless of currency
        return sum + (item.totalAmount || 0);
      } else {
        // Filter by currency - ensure AED stock balance is included when filter is AED
        const itemCurrency = String(item.currency || '').trim().toUpperCase();
        const filterCurrency = String(currencyFilter).trim().toUpperCase();
        // Match currency exactly (handles both 'INR' and 'AED')
        if (itemCurrency === filterCurrency) {
          return sum + (item.totalAmount || 0);
        }
        return sum;
      }
    }, 0);

    // Overall Balance = Overall Profit + Stock Balance (always add)
    // Which equals: (A1 + Transactions - Expenses) + Stock Balance
    const overallBalanceWithStock = profitData.overallProfit + stockBalanceTotalAmount;

    return {
      overallProfit: profitData.overallProfit,
      stockBalanceTotalAmount,
      overallBalanceWithStock,
      currency: currencyFilter
    };
  };

  // Calculate charity allocation for a period (1% of overall profit)
  const calculateCharityAllocation = (period = 'all', currencyFilter = null) => {
    const profitData = calculateOverallProfit(period, currencyFilter);
    const filteredTable2 = filterByPeriod(tableData.table2, period);
    
    // Filter expenses by currency if specified
    const currencyFilteredTable2 = currencyFilter 
      ? filteredTable2.filter(item => String(item.currency || '').toUpperCase().trim() === currencyFilter)
      : filteredTable2;
    
    const charityRate = 0.01; // 1% of overall profit
    const charityTarget = Math.max(profitData.overallProfit * charityRate, 0);
    const charitySpent = currencyFilteredTable2.reduce((sum, item) => {
      const purpose = String(item.purpose || '').toLowerCase().trim();
      // Check for charity-related keywords
      if (purpose.includes('charity') || purpose.includes('charitable') || purpose === 'charity') {
        const amount = parseFloat(item.amount) || 0;
        console.log(`[Charity] Found charity expense: ${purpose}, Amount: ${amount}, Currency: ${item.currency}`);
        return sum + amount;
      }
      return sum;
    }, 0);
    
    // Debug: Log all expenses to see what we're processing
    if (currencyFilteredTable2.length > 0) {
      console.log(`[Charity] Processing ${currencyFilteredTable2.length} expenses for ${currencyFilter || 'all currencies'}`);
      console.log(`[Charity] Sample expenses:`, currencyFilteredTable2.slice(0, 3).map(e => ({ purpose: e.purpose, amount: e.amount, currency: e.currency })));
    }
    const charityRemaining = Math.max(charityTarget - charitySpent, 0);
    const charitySurplus = Math.max(charitySpent - charityTarget, 0);

    return {
      charityTarget,
      charitySpent,
      charityRemaining,
      charitySurplus
    };
  };

  // Get Source performance for a period (from Stock Updates - table3)
  const getSourcePerformance = (period = 'all', limit = 10) => {
    let filteredData = tableData.table3;
    
    // Filter by period if needed (table3 might have dates in the future)
    if (period !== 'all') {
      filteredData = filterByPeriod(tableData.table3, period);
    }
    
    const totalsBySource = {};

    filteredData.forEach(item => {
      const source = item.code || 'Unknown'; // In table3, 'code' is actually 'Source'
      if (!totalsBySource[source]) {
        totalsBySource[source] = {
          name: source,
          totalAmount: 0
        };
      }
      totalsBySource[source].totalAmount += item.amount || 0;
    });

    return Object.values(totalsBySource)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, limit);
  };

  // Get Client performance by profit for a period (from Transactions - table4)
  const getClientPerformanceByProfit = (period = 'all', limit = 10) => {
    const filteredTable4 = filterByPeriod(tableData.table4, period);
    const totalsByClient = {};

    filteredTable4.forEach(item => {
      const key = item.name || 'Unknown';
      if (!totalsByClient[key]) {
        totalsByClient[key] = {
          name: key,
          code: item.code || '-',
          totalNetProfit: 0
        };
      }
      totalsByClient[key].totalNetProfit += item.netProfit || 0;
    });

    return Object.values(totalsByClient)
      .sort((a, b) => b.totalNetProfit - a.totalNetProfit)
      .slice(0, limit);
  };

  // Get Client performance by amount for a period (from Transactions - table4)
  const getClientPerformanceByAmount = (period = 'all', limit = 10) => {
    const filteredTable4 = filterByPeriod(tableData.table4, period);
    const totalsByClient = {};

    filteredTable4.forEach(item => {
      const key = item.name || 'Unknown';
      if (!totalsByClient[key]) {
        totalsByClient[key] = {
          name: key,
          code: item.code || '-',
          totalAmount: 0
        };
      }
      // Use INR amount for total
      totalsByClient[key].totalAmount += item.inr || 0;
    });

    return Object.values(totalsByClient)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, limit);
  };

  // Get available currencies from table data
  const getAvailableCurrencies = () => {
    const currencies = new Set();
    tableData.table1.forEach(item => {
      if (item.currency) currencies.add(String(item.currency).toUpperCase().trim());
    });
    tableData.table2.forEach(item => {
      if (item.currency) currencies.add(String(item.currency).toUpperCase().trim());
    });
    return Array.from(currencies).sort();
  };

  const availableCurrencies = getAvailableCurrencies();

  // Calculate all metrics for all periods with currency filter (only for INR/AED tabs)
  const currencyFilter = (activeTab === 'INR' || activeTab === 'AED') ? activeTab : null;
  const currentMonthProfit = calculateOverallProfit('currentMonth', currencyFilter);
  const currentProfit = calculateOverallProfit('current', currencyFilter);
  const lastMonthProfit = calculateOverallProfit('lastMonth', currencyFilter);
  const lastYearProfit = calculateOverallProfit('lastYear', currencyFilter);
  const allTimeProfit = calculateOverallProfit('all', currencyFilter);

  const currentMonthBalance = calculateOverallBalance('currentMonth', currencyFilter);
  const currentBalance = calculateOverallBalance('current', currencyFilter);
  const lastMonthBalance = calculateOverallBalance('lastMonth', currencyFilter);
  const lastYearBalance = calculateOverallBalance('lastYear', currencyFilter);
  const allTimeBalance = calculateOverallBalance('all', currencyFilter);

  // Calculate charity stats for all periods
  const currentMonthCharity = calculateCharityAllocation('currentMonth', currencyFilter);
  const currentCharity = calculateCharityAllocation('current', currencyFilter);
  const lastMonthCharity = calculateCharityAllocation('lastMonth', currencyFilter);
  const lastYearCharity = calculateCharityAllocation('lastYear', currencyFilter);
  const allTimeCharity = calculateCharityAllocation('all', currencyFilter);

  // Money Tracker Statistics
  const calculateMoneyTrackerStats = () => {
    const allMoneyIn = tableData.table7_moneyin || [];
    const allMoneyOut = tableData.table7_moneyout || [];
    
    const stats = {
      moneyIn: {
        totalReceived: 0,
        totalCharges: 0,
        totalReturnable: 0,
        totalCollected: 0,
        outstanding: 0,
        cleared: 0,
        pending: 0,
        notStarted: 0,
        totalEntries: allMoneyIn.length
      },
      moneyOut: {
        totalReceived: 0,
        totalCharges: 0,
        totalReturnable: 0,
        totalCollected: 0,
        outstanding: 0,
        cleared: 0,
        pending: 0,
        notStarted: 0,
        totalEntries: allMoneyOut.length
      },
      combined: {
        totalReceived: 0,
        totalCharges: 0,
        totalReturnable: 0,
        totalCollected: 0,
        outstanding: 0,
        netFlow: 0
      }
    };

    // Calculate Money In stats
    allMoneyIn.forEach(item => {
      stats.moneyIn.totalReceived += item.receivedAmt || 0;
      stats.moneyIn.totalCharges += item.charges || 0;
      stats.moneyIn.totalReturnable += item.totalReturnable || 0;
      stats.moneyIn.outstanding += item.balance || 0;
      stats.moneyIn.totalCollected += (item.totalReturnable || 0) - (item.balance || 0);
      
      const remarks = String(item.remarks || '').toLowerCase();
      if (remarks === 'cleared' || Math.abs(item.balance || 0) < 0.01) {
        stats.moneyIn.cleared++;
      } else if (remarks === 'pending') {
        stats.moneyIn.pending++;
      } else {
        stats.moneyIn.notStarted++;
      }
    });

    // Calculate Money Out stats
    allMoneyOut.forEach(item => {
      stats.moneyOut.totalReceived += item.receivedAmt || 0;
      stats.moneyOut.totalCharges += item.charges || 0;
      stats.moneyOut.totalReturnable += item.totalReturnable || 0;
      stats.moneyOut.outstanding += item.balance || 0;
      stats.moneyOut.totalCollected += (item.totalReturnable || 0) - (item.balance || 0);
      
      const remarks = String(item.remarks || '').toLowerCase();
      if (remarks === 'cleared' || Math.abs(item.balance || 0) < 0.01) {
        stats.moneyOut.cleared++;
      } else if (remarks === 'pending') {
        stats.moneyOut.pending++;
      } else {
        stats.moneyOut.notStarted++;
      }
    });

    // Combined stats
    stats.combined.totalReceived = stats.moneyIn.totalReceived + stats.moneyOut.totalReceived;
    stats.combined.totalCharges = stats.moneyIn.totalCharges + stats.moneyOut.totalCharges;
    stats.combined.totalReturnable = stats.moneyIn.totalReturnable + stats.moneyOut.totalReturnable;
    stats.combined.totalCollected = stats.moneyIn.totalCollected + stats.moneyOut.totalCollected;
    stats.combined.outstanding = stats.moneyIn.outstanding + stats.moneyOut.outstanding;
    stats.combined.netFlow = stats.moneyIn.totalReceived - stats.moneyOut.totalReceived;

    return stats;
  };

  const moneyTrackerStats = calculateMoneyTrackerStats();

  // Get top clients/sources by Money In
  const getTopMoneyInClients = (limit = 10) => {
    const totals = {};
    tableData.table7_moneyin.forEach(item => {
      const key = item.name || 'Unknown';
      if (!totals[key]) {
        totals[key] = {
          name: key,
          code: item.code || '-',
          totalReceived: 0,
          totalReturnable: 0,
          outstanding: 0,
          count: 0
        };
      }
      totals[key].totalReceived += item.receivedAmt || 0;
      totals[key].totalReturnable += item.totalReturnable || 0;
      totals[key].outstanding += item.balance || 0;
      totals[key].count++;
    });
    return Object.values(totals)
      .sort((a, b) => b.totalReceived - a.totalReceived)
      .slice(0, limit);
  };

  // Get top clients/sources by Money Out
  const getTopMoneyOutClients = (limit = 10) => {
    const totals = {};
    tableData.table7_moneyout.forEach(item => {
      const key = item.name || 'Unknown';
      if (!totals[key]) {
        totals[key] = {
          name: key,
          code: item.code || '-',
          totalReceived: 0,
          totalReturnable: 0,
          outstanding: 0,
          count: 0
        };
      }
      totals[key].totalReceived += item.receivedAmt || 0;
      totals[key].totalReturnable += item.totalReturnable || 0;
      totals[key].outstanding += item.balance || 0;
      totals[key].count++;
    });
    return Object.values(totals)
      .sort((a, b) => b.totalReceived - a.totalReceived)
      .slice(0, limit);
  };

  // Get monthly trends for Money Tracker
  const getMoneyTrackerMonthlyTrends = () => {
    const monthlyData = {};
    
    // Process Money In
    tableData.table7_moneyin.forEach(item => {
      if (!item.receivedDate) return;
      const dateParts = String(item.receivedDate).split('/');
      if (dateParts.length !== 3) return;
      
      const month = parseInt(dateParts[1], 10);
      const year = parseInt(dateParts[2], 10);
      const key = `${year}-${String(month).padStart(2, '0')}`;
      
      if (!monthlyData[key]) {
        monthlyData[key] = {
          month: `${dateParts[1]}/${year}`,
          moneyIn: 0,
          moneyOut: 0,
          netFlow: 0
        };
      }
      
      monthlyData[key].moneyIn += item.receivedAmt || 0;
    });
    
    // Process Money Out
    tableData.table7_moneyout.forEach(item => {
      if (!item.receivedDate) return;
      const dateParts = String(item.receivedDate).split('/');
      if (dateParts.length !== 3) return;
      
      const month = parseInt(dateParts[1], 10);
      const year = parseInt(dateParts[2], 10);
      const key = `${year}-${String(month).padStart(2, '0')}`;
      
      if (!monthlyData[key]) {
        monthlyData[key] = {
          month: `${dateParts[1]}/${year}`,
          moneyIn: 0,
          moneyOut: 0,
          netFlow: 0
        };
      }
      
      monthlyData[key].moneyOut += item.receivedAmt || 0;
    });
    
    Object.keys(monthlyData).forEach(key => {
      monthlyData[key].netFlow = monthlyData[key].moneyIn - monthlyData[key].moneyOut;
    });
    
    return Object.values(monthlyData)
      .sort((a, b) => {
        const aParts = a.month.split('/');
        const bParts = b.month.split('/');
        if (aParts[1] !== bParts[1]) return parseInt(aParts[1]) - parseInt(bParts[1]);
        return parseInt(aParts[0]) - parseInt(bParts[0]);
      });
  };

  const monthlyTrends = getMoneyTrackerMonthlyTrends();

  // Get current year period
  const getCurrentYearPeriod = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    return {
      startDate: new Date(currentYear, 0, 1),
      endDate: new Date(currentYear, 11, 31, 23, 59, 59),
      year: currentYear
    };
  };

  // Get previous year period
  const getPreviousYearPeriod = () => {
    const now = new Date();
    const previousYear = now.getFullYear() - 1;
    return {
      startDate: new Date(previousYear, 0, 1),
      endDate: new Date(previousYear, 11, 31, 23, 59, 59),
      year: previousYear
    };
  };

  // Count unique Sources from table5 (Source and Client table)
  // Hybrid entries count as both Source and Client
  const countUniqueSources = () => {
    const sources = new Set();
    tableData.table5.forEach(item => {
      const type = String(item.type || '').trim().toUpperCase();
      if (type === 'SOURCE' || type === 'HYBRID') {
        const name = String(item.name || '').trim();
        if (name) {
          sources.add(name);
        }
      }
    });
    return sources.size;
  };

  // Calculate comprehensive Source statistics
  const calculateSourceStats = (period = 'all') => {
    const data = getSourcePerformance(period, 1000); // Get all sources, not just top 10
    const currentYearPeriod = getCurrentYearPeriod();
    const previousYearPeriod = getPreviousYearPeriod();
    
    const totalAmount = data.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
    const totalSources = countUniqueSources(); // Count from table5, not from performance data
    const averagePerSource = totalSources > 0 ? totalAmount / totalSources : 0;
    const topSource = data.length > 0 ? data[0] : null;
    
    // Current year data (filter for current year)
    const currentYearData = tableData.table3.filter(item => {
      if (!item.date) return false;
      const dateInfo = parseDate(item.date);
      if (!dateInfo) return false;
      const currentYear = getCurrentDateInfo().year;
      return dateInfo.year === currentYear;
    });
    const currentYearTotal = currentYearData.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Previous year data
    const previousYearData = tableData.table3.filter(item => {
      if (!item.date) return false;
      const dateInfo = parseDate(item.date);
      if (!dateInfo) return false;
      const previousYear = getCurrentDateInfo().year - 1;
      return dateInfo.year === previousYear;
    });
    const previousYearTotal = previousYearData.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Calculate YoY growth
    const yoyGrowth = previousYearTotal > 0 
      ? ((currentYearTotal - previousYearTotal) / previousYearTotal) * 100 
      : 0;
    
    // Calculate market share for top source
    const topSourceMarketShare = totalAmount > 0 && topSource 
      ? (topSource.totalAmount / totalAmount) * 100 
      : 0;
    
    return {
      totalAmount,
      totalSources,
      averagePerSource,
      topSource,
      topSourceMarketShare,
      currentYearTotal,
      previousYearTotal,
      yoyGrowth
    };
  };

  // Count unique Clients from table5 (Source and Client table)
  // Hybrid entries count as both Source and Client
  const countUniqueClients = () => {
    const clients = new Set();
    tableData.table5.forEach(item => {
      const type = String(item.type || '').trim().toUpperCase();
      if (type === 'CLIENT' || type === 'HYBRID') {
        const name = String(item.name || '').trim();
        if (name) {
          clients.add(name);
        }
      }
    });
    return clients.size;
  };

  // Calculate comprehensive Client statistics
  const calculateClientStats = (period = 'all', byProfit = true) => {
    const currentYearPeriod = getCurrentYearPeriod();
    const previousYearPeriod = getPreviousYearPeriod();
    
    // Get current period data
    const periodData = byProfit 
      ? getClientPerformanceByProfit(period, 1000)
      : getClientPerformanceByAmount(period, 1000);
    
    const valueKey = byProfit ? 'totalNetProfit' : 'totalAmount';
    const totalValue = periodData.reduce((sum, item) => sum + (item[valueKey] || 0), 0);
    const totalClients = countUniqueClients(); // Count from table5, not from performance data
    const averagePerClient = totalClients > 0 ? totalValue / totalClients : 0;
    const topClient = periodData.length > 0 ? periodData[0] : null;
    
    // Current year data (filter for current year)
    const currentYear = getCurrentDateInfo().year;
    const currentYearData = tableData.table4.filter(item => {
      if (!item.date) return false;
      const dateInfo = parseDate(item.date);
      return dateInfo && dateInfo.year === currentYear;
    });
    const currentYearTotal = currentYearData.reduce((sum, item) => {
      return sum + (byProfit ? (item.netProfit || 0) : (item.inr || 0));
    }, 0);
    
    // Previous year data  
    const previousYear = currentYear - 1;
    const previousYearData = tableData.table4.filter(item => {
      if (!item.date) return false;
      const dateInfo = parseDate(item.date);
      return dateInfo && dateInfo.year === previousYear;
    });
    const previousYearTotal = previousYearData.reduce((sum, item) => {
      return sum + (byProfit ? (item.netProfit || 0) : (item.inr || 0));
    }, 0);
    
    // Calculate YoY growth
    const yoyGrowth = previousYearTotal > 0 
      ? ((currentYearTotal - previousYearTotal) / previousYearTotal) * 100 
      : 0;
    
    // Calculate market share for top client
    const topClientMarketShare = totalValue > 0 && topClient 
      ? (topClient[valueKey] / totalValue) * 100 
      : 0;
    
    return {
      totalValue,
      totalClients,
      averagePerClient,
      topClient,
      topClientMarketShare,
      currentYearTotal,
      previousYearTotal,
      yoyGrowth
    };
  };

  // Source performance data - limit to 5 rankings
  const allTimeSourcePerformance = getSourcePerformance('all', 5);
  const previousYearSourcePerformance = getSourcePerformance('lastYear', 5);
  const currentYearSourcePerformance = getSourcePerformance('current', 5);
  const previousMonthSourcePerformance = getSourcePerformance('lastMonth', 5);
  const currentMonthSourcePerformance = getSourcePerformance('currentMonth', 5);

  // Source statistics
  const allTimeSourceStats = calculateSourceStats('all');
  const currentYearSourceStats = calculateSourceStats('current');
  const lastYearSourceStats = calculateSourceStats('lastYear');

  // Client performance data - limit to 5 rankings
  const allTimeClientByProfit = getClientPerformanceByProfit('all', 5);
  const previousYearClientByProfit = getClientPerformanceByProfit('lastYear', 5);
  const currentYearClientByProfit = getClientPerformanceByProfit('current', 5);
  const previousMonthClientByProfit = getClientPerformanceByProfit('lastMonth', 5);
  const currentMonthClientByProfit = getClientPerformanceByProfit('currentMonth', 5);

  const allTimeClientByAmount = getClientPerformanceByAmount('all', 5);
  const previousYearClientByAmount = getClientPerformanceByAmount('lastYear', 5);
  const currentYearClientByAmount = getClientPerformanceByAmount('current', 5);
  const previousMonthClientByAmount = getClientPerformanceByAmount('lastMonth', 5);
  const currentMonthClientByAmount = getClientPerformanceByAmount('currentMonth', 5);

  // Client statistics
  const allTimeClientStatsProfit = calculateClientStats('all', true);
  const currentYearClientStatsProfit = calculateClientStats('current', true);
  const lastYearClientStatsProfit = calculateClientStats('lastYear', true);

  const allTimeClientStatsAmount = calculateClientStats('all', false);
  const currentYearClientStatsAmount = calculateClientStats('current', false);
  const lastYearClientStatsAmount = calculateClientStats('lastYear', false);

  // Card Component
  const MetricCard = ({ title, value, subtitle, trend, period, currency, isCount = false }) => {
    const isPositive = value >= 0;
    const valueColor = isPositive ? '#10b981' : '#ef4444';
    const bgColor = isPositive ? '#f0fdf4' : '#fef2f2';
    const borderColor = isPositive ? '#10b981' : '#ef4444';
    const currencySymbol = currency === 'INR' ? '₹' : 'AED ';

    return (
      <div className="metric-card">
        <div className="metric-card-header">
          <h3 className="metric-card-title">{title}</h3>
          <span className="metric-card-period">{period}</span>
        </div>
        <div className="metric-card-body">
          <div className="metric-card-value" style={{ color: valueColor }}>
            {isCount 
              ? Math.abs(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })
              : `${currencySymbol}${Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            }
          </div>
          {subtitle && <div className="metric-card-subtitle">{subtitle}</div>}
          {trend !== undefined && (
            <div className="metric-card-trend" style={{ color: trend >= 0 ? '#10b981' : '#ef4444' }}>
              {trend >= 0 ? '↗' : '↘'} {Math.abs(trend).toFixed(2)}%
            </div>
          )}
        </div>
      </div>
    );
  };

  const ListCard = ({ title, items, period, itemLabel, valueKey }) => {
    return (
      <div className="metric-card">
        <div className="metric-card-header">
          <h3 className="metric-card-title">{title}</h3>
          <span className="metric-card-period">{period}</span>
        </div>
        <div className="metric-card-body">
          {items.length > 0 ? (
            <div className="metric-list">
              {items.map((item, index) => (
                <div key={`${item.name}-${item.code || ''}-${index}`} className="metric-list-item">
                  <div className="metric-list-info">
                    <span className="metric-list-rank">#{index + 1}</span>
                    <div className="metric-list-details">
                      <span className="metric-list-name">{item.name || 'Unnamed'}</span>
                      {item.code && <span className="metric-list-code">Code: {item.code}</span>}
                    </div>
                  </div>
                  <span className="metric-list-value">
                    ₹{item[valueKey].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="metric-empty">No data available</div>
          )}
        </div>
      </div>
    );
  };

  // Helper function to get period label
  const getPeriodLabel = (period) => {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    if (period === 'all') return 'All Time';
    if (period === 'current') {
      return `Current Year (${now.getFullYear()})`;
    }
    if (period === 'currentMonth') {
      const current = getCurrentDateInfo();
      return `${monthNames[current.month - 1]} ${current.year}`;
    }
    if (period === 'lastMonth') {
      const lastMonth = getLastMonthDateInfo();
      return `${monthNames[lastMonth.month - 1]} ${lastMonth.year}`;
    }
    if (period === 'lastYear') {
      return `Full Year ${now.getFullYear() - 1}`;
    }
    return period;
  };

  // Helper functions for charts
  const netProfitByMonth = () => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = getCurrentDateInfo().year;
    const monthlyProfit = {};
    
    tableData.table1.forEach(item => {
      const dateInfo = parseDate(item.date);
      if (dateInfo && dateInfo.year === currentYear) {
        const monthKey = `${dateInfo.year}-${dateInfo.month}`;
        if (!monthlyProfit[monthKey]) {
          monthlyProfit[monthKey] = {
            month: dateInfo.month,
            year: dateInfo.year,
            netProfit: 0
          };
        }
        monthlyProfit[monthKey].netProfit += item.netProfit || 0;
      }
    });

    const result = [];
    for (let i = 1; i <= 12; i++) {
      const monthKey = `${currentYear}-${i}`;
      if (monthlyProfit[monthKey]) {
        result.push({
          month: monthNames[i - 1],
          netProfit: monthlyProfit[monthKey].netProfit
        });
      } else {
        result.push({
          month: monthNames[i - 1],
          netProfit: 0
        });
      }
    }
    
    return result;
  };

  const netProfitByYear = () => {
    const yearlyProfit = {};
    
    tableData.table1.forEach(item => {
      const dateInfo = parseDate(item.date);
      if (dateInfo) {
        const year = dateInfo.year;
        if (!yearlyProfit[year]) {
          yearlyProfit[year] = {
            year: year,
            netProfit: 0
          };
        }
        yearlyProfit[year].netProfit += item.netProfit || 0;
      }
    });

    return Object.values(yearlyProfit)
      .sort((a, b) => a.year - b.year)
      .map(item => ({
        year: item.year.toString(),
        netProfit: item.netProfit
      }));
  };

  // Expenses by Month
  const expensesByMonth = () => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = getCurrentDateInfo().year;
    const monthlyExpenses = {};
    
    tableData.table2.forEach(item => {
      const dateInfo = parseDate(item.date);
      if (dateInfo && dateInfo.year === currentYear) {
        const monthKey = `${dateInfo.year}-${dateInfo.month}`;
        if (!monthlyExpenses[monthKey]) {
          monthlyExpenses[monthKey] = {
            month: dateInfo.month,
            year: dateInfo.year,
            totalAmount: 0
          };
        }
        monthlyExpenses[monthKey].totalAmount += item.amount || 0;
      }
    });

    const result = [];
    for (let i = 1; i <= 12; i++) {
      const monthKey = `${currentYear}-${i}`;
      if (monthlyExpenses[monthKey]) {
        result.push({
          month: monthNames[i - 1],
          totalAmount: monthlyExpenses[monthKey].totalAmount
        });
      } else {
        result.push({
          month: monthNames[i - 1],
          totalAmount: 0
        });
      }
    }
    
    return result;
  };

  // Expenses by Year
  const expensesByYear = () => {
    const yearlyExpenses = {};
    
    tableData.table2.forEach(item => {
      const dateInfo = parseDate(item.date);
      if (dateInfo) {
        const year = dateInfo.year;
        if (!yearlyExpenses[year]) {
          yearlyExpenses[year] = {
            year: year,
            totalAmount: 0
          };
        }
        yearlyExpenses[year].totalAmount += item.amount || 0;
      }
    });

    return Object.values(yearlyExpenses)
      .sort((a, b) => a.year - b.year)
      .map(item => ({
        year: item.year.toString(),
        totalAmount: item.totalAmount
      }));
  };

  // Transactions Net-Profit by Month
  const transactionsNetProfitByMonth = () => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = getCurrentDateInfo().year;
    const monthlyProfit = {};
    
    tableData.table4.forEach(item => {
      const dateInfo = parseDate(item.date);
      if (dateInfo && dateInfo.year === currentYear) {
        const monthKey = `${dateInfo.year}-${dateInfo.month}`;
        if (!monthlyProfit[monthKey]) {
          monthlyProfit[monthKey] = {
            month: dateInfo.month,
            year: dateInfo.year,
            netProfit: 0
          };
        }
        monthlyProfit[monthKey].netProfit += item.netProfit || 0;
      }
    });

    const result = [];
    for (let i = 1; i <= 12; i++) {
      const monthKey = `${currentYear}-${i}`;
      if (monthlyProfit[monthKey]) {
        result.push({
          month: monthNames[i - 1],
          netProfit: monthlyProfit[monthKey].netProfit
        });
      } else {
        result.push({
          month: monthNames[i - 1],
          netProfit: 0
        });
      }
    }
    
    return result;
  };

  // Transactions Net-Profit by Year
  const transactionsNetProfitByYear = () => {
    const yearlyProfit = {};
    
    tableData.table4.forEach(item => {
      const dateInfo = parseDate(item.date);
      if (dateInfo) {
        const year = dateInfo.year;
        if (!yearlyProfit[year]) {
          yearlyProfit[year] = {
            year: year,
            netProfit: 0
          };
        }
        yearlyProfit[year].netProfit += item.netProfit || 0;
      }
    });

    return Object.values(yearlyProfit)
      .sort((a, b) => a.year - b.year)
      .map(item => ({
        year: item.year.toString(),
        netProfit: item.netProfit
      }));
  };

  // Export Dashboard as PDF
  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const doc = new jsPDF();
      let yPosition = 20;
      const margin = 20;
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const maxY = pageHeight - margin;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredHeight = 20) => {
        if (yPosition + requiredHeight > maxY) {
          doc.addPage();
          yPosition = 20;
        }
      };

      // Add title
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text(`Dashboard - ${activeTab} Report`, margin, yPosition);
      yPosition += 15;

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const currentDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      doc.text(`Generated on: ${currentDate}`, margin, yPosition);
      yPosition += 15;

      if (activeTab === 'INR' || activeTab === 'AED') {
        // Export INR/AED metrics
        const currency = activeTab;
        const currencySymbol = currency === 'INR' ? '₹' : 'AED ';

        // Recalculate values for the current currency filter
        const exportCurrencyFilter = currency;
        const exportLastMonthBalance = calculateOverallBalance('lastMonth', exportCurrencyFilter);
        const exportLastYearBalance = calculateOverallBalance('lastYear', exportCurrencyFilter);
        const exportLastMonthProfit = calculateOverallProfit('lastMonth', exportCurrencyFilter);
        const exportLastYearProfit = calculateOverallProfit('lastYear', exportCurrencyFilter);
        const exportLastMonthCharity = calculateCharityAllocation('lastMonth', exportCurrencyFilter);
        const exportLastYearCharity = calculateCharityAllocation('lastYear', exportCurrencyFilter);

        // Overall Balance
        checkPageBreak(30);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(`Overall Balance (${currency})`, margin, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const lastMonthBal = exportLastMonthBalance?.overallBalanceWithStock || 0;
        const lastYearBal = exportLastYearBalance?.overallBalanceWithStock || 0;
        doc.text(`Last Month: ${currencySymbol}${lastMonthBal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 7;
        doc.text(`Last Year: ${currencySymbol}${lastYearBal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 15;

        // Overall Profit
        checkPageBreak(30);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(`Overall Profit (${currency})`, margin, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const lastMonthProf = exportLastMonthProfit?.overallProfit || 0;
        const lastYearProf = exportLastYearProfit?.overallProfit || 0;
        doc.text(`Last Month: ${currencySymbol}${lastMonthProf.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 7;
        doc.text(`Last Year: ${currencySymbol}${lastYearProf.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 15;

        // Charity Allocation (1% of Overall Profit)
        checkPageBreak(40);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(`Charity Allocation (${currency}) - 1% of Profit`, margin, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const lastMonthCharityTarget = exportLastMonthCharity?.charityTarget || 0;
        const lastMonthCharitySpent = exportLastMonthCharity?.charitySpent || 0;
        const lastMonthCharityRemaining = exportLastMonthCharity?.charityRemaining || 0;
        const lastMonthCharitySurplus = exportLastMonthCharity?.charitySurplus || 0;
        doc.text(`Last Month - Target: ${currencySymbol}${lastMonthCharityTarget.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 7;
        doc.text(`Last Month - Spent: ${currencySymbol}${lastMonthCharitySpent.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 7;
        if (lastMonthCharityRemaining > 0) {
          doc.text(`Last Month - Remaining: ${currencySymbol}${lastMonthCharityRemaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        } else {
          doc.text(`Last Month - Surplus: ${currencySymbol}${lastMonthCharitySurplus.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        }
        yPosition += 10;

        const lastYearCharityTarget = exportLastYearCharity?.charityTarget || 0;
        const lastYearCharitySpent = exportLastYearCharity?.charitySpent || 0;
        const lastYearCharityRemaining = exportLastYearCharity?.charityRemaining || 0;
        const lastYearCharitySurplus = exportLastYearCharity?.charitySurplus || 0;
        doc.text(`Last Year - Target: ${currencySymbol}${lastYearCharityTarget.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 7;
        doc.text(`Last Year - Spent: ${currencySymbol}${lastYearCharitySpent.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 7;
        if (lastYearCharityRemaining > 0) {
          doc.text(`Last Year - Remaining: ${currencySymbol}${lastYearCharityRemaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        } else {
          doc.text(`Last Year - Surplus: ${currencySymbol}${lastYearCharitySurplus.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        }
        yPosition += 15;

        // Monthly Profit Chart Data
        checkPageBreak(50);
        const monthlyData = netProfitByMonth();
        if (monthlyData && monthlyData.length > 0) {
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text(`Monthly Profit (${currency})`, margin, yPosition);
          yPosition += 10;

          const tableData = monthlyData.map(item => [
            item.month,
            `${currencySymbol}${(item.netProfit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          ]);

          autoTable(doc, {
            startY: yPosition,
            head: [['Month', 'Net Profit']],
            body: tableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 9 },
            headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [249, 250, 251] },
          });

          yPosition = doc.lastAutoTable.finalY + 15;
        }

        // Yearly Profit Chart Data
        checkPageBreak(50);
        const yearlyData = netProfitByYear();
        if (yearlyData && yearlyData.length > 0) {
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text(`Yearly Profit (${currency})`, margin, yPosition);
          yPosition += 10;

          const tableData = yearlyData.map(item => [
            item.year,
            `${currencySymbol}${(item.netProfit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          ]);

          autoTable(doc, {
            startY: yPosition,
            head: [['Year', 'Net Profit']],
            body: tableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 9 },
            headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [249, 250, 251] },
          });

          yPosition = doc.lastAutoTable.finalY + 15;
        }

      } else if (activeTab === 'Source') {
        // Export Source metrics
        checkPageBreak(30);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Source Performance', margin, yPosition);
        yPosition += 10;

        // Source Statistics
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Statistics', margin, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const allTimeStats = allTimeSourceStats || {};
        doc.text(`Total Sources: ${allTimeStats.totalSources || 0}`, margin, yPosition);
        yPosition += 7;
        doc.text(`Total Amount: ₹${(allTimeStats.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 7;
        doc.text(`Average per Source: ₹${(allTimeStats.averagePerSource || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 15;

        // Top Sources
        checkPageBreak(50);
        if (allTimeSourcePerformance && allTimeSourcePerformance.length > 0) {
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text('Top Sources (All Time)', margin, yPosition);
          yPosition += 10;

          const tableData = allTimeSourcePerformance.map((item, index) => [
            (index + 1).toString(),
            item.name || 'Unknown',
            `₹${(item.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          ]);

          autoTable(doc, {
            startY: yPosition,
            head: [['Rank', 'Source', 'Total Amount']],
            body: tableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 9 },
            headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [249, 250, 251] },
          });

          yPosition = doc.lastAutoTable.finalY + 15;
        }

      } else if (activeTab === 'Client') {
        // Export Client metrics
        checkPageBreak(30);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(`Client Performance (${activeClientSubTab === 'profit' ? 'By Profit' : 'By Amount'})`, margin, yPosition);
        yPosition += 10;

        // Client Statistics
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Statistics', margin, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const clientStats = (activeClientSubTab === 'profit' ? allTimeClientStatsProfit : allTimeClientStatsAmount) || {};
        doc.text(`Total Clients: ${clientStats.totalClients || 0}`, margin, yPosition);
        yPosition += 7;
        const valueKey = activeClientSubTab === 'profit' ? 'totalNetProfit' : 'totalAmount';
        const valueLabel = activeClientSubTab === 'profit' ? 'Total Profit' : 'Total Amount';
        doc.text(`${valueLabel}: ₹${(clientStats.totalValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 7;
        doc.text(`Average per Client: ₹${(clientStats.averagePerClient || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
        yPosition += 15;

        // Top Clients
        checkPageBreak(50);
        const clientPerformance = activeClientSubTab === 'profit' ? allTimeClientByProfit : allTimeClientByAmount;
        if (clientPerformance && clientPerformance.length > 0) {
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text(`Top Clients (All Time) - ${activeClientSubTab === 'profit' ? 'By Profit' : 'By Amount'}`, margin, yPosition);
          yPosition += 10;

          const tableData = clientPerformance.map((item, index) => [
            (index + 1).toString(),
            item.name || 'Unknown',
            `₹${(item[valueKey] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          ]);

          autoTable(doc, {
            startY: yPosition,
            head: [['Rank', 'Client', activeClientSubTab === 'profit' ? 'Total Profit' : 'Total Amount']],
            body: tableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 9 },
            headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [249, 250, 251] },
          });

          yPosition = doc.lastAutoTable.finalY + 15;
        }
      }

      // Save PDF
      const filename = `Dashboard_${activeTab}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      
      alert('Dashboard exported successfully!');
    } catch (error) {
      console.error('Error exporting dashboard:', error);
      alert(`Failed to export dashboard: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="dashboard-subtitle">Analytics and Insights</p>
      </div>

      {/* Main Tabs */}
      <div className="dashboard-section">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '32px', gap: '16px', flexWrap: 'wrap' }}>
          <div className="currency-tabs">
            <button
              className={`currency-tab ${activeTab === 'INR' ? 'active' : ''}`}
              onClick={() => setActiveTab('INR')}
            >
              INR
            </button>
            <button
              className={`currency-tab ${activeTab === 'AED' ? 'active' : ''}`}
              onClick={() => setActiveTab('AED')}
            >
              AED
            </button>
            <button
              className={`currency-tab ${activeTab === 'Source' ? 'active' : ''}`}
              onClick={() => setActiveTab('Source')}
            >
              Source
            </button>
            <button
              className={`currency-tab ${activeTab === 'Client' ? 'active' : ''}`}
              onClick={() => setActiveTab('Client')}
            >
              Client
            </button>
            <button
              className={`currency-tab ${activeTab === 'Money Tracker' ? 'active' : ''}`}
              onClick={() => setActiveTab('Money Tracker')}
            >
              Money Tracker
            </button>
          </div>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            style={{
              padding: '10px 20px',
              backgroundColor: exporting ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: exporting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            onMouseOver={(e) => {
              if (!exporting) {
                e.target.style.backgroundColor = '#2563eb';
              }
            }}
            onMouseOut={(e) => {
              if (!exporting) {
                e.target.style.backgroundColor = '#3b82f6';
              }
            }}
          >
            {exporting ? '⏳ Exporting...' : '📄 Export PDF'}
          </button>
        </div>

        {/* INR/AED Tab Content */}
        {(activeTab === 'INR' || activeTab === 'AED') && (
          <>
            {/* Balance Snapshot */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Balance Snapshot ({activeTab})</h2>
              <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <MetricCard 
                  title="Current Month Net Balance"
                  value={currentMonthBalance.overallBalanceWithStock}
                  subtitle={`Profit: ${activeTab === 'INR' ? '₹' : 'AED '}${currentMonthBalance.overallProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Stock: ${activeTab === 'INR' ? '₹' : 'AED '}${currentMonthBalance.stockBalanceTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period={getPeriodLabel('currentMonth')}
                  currency={activeTab}
                />
                <MetricCard 
                  title="Last Month Net Balance"
                  value={lastMonthBalance.overallBalanceWithStock}
                  subtitle={`Profit: ${activeTab === 'INR' ? '₹' : 'AED '}${lastMonthBalance.overallProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Stock: ${activeTab === 'INR' ? '₹' : 'AED '}${lastMonthBalance.stockBalanceTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period={getPeriodLabel('lastMonth')}
                  currency={activeTab}
                />
                <MetricCard 
                  title="Last Year Net Balance"
                  value={lastYearBalance.overallBalanceWithStock}
                  subtitle={`Profit: ${activeTab === 'INR' ? '₹' : 'AED '}${lastYearBalance.overallProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Stock: ${activeTab === 'INR' ? '₹' : 'AED '}${lastYearBalance.stockBalanceTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period={getPeriodLabel('lastYear')}
                  currency={activeTab}
                />
              </div>
            </div>

            {/* Profit Overview */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Profit Overview ({activeTab})</h2>
              <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <MetricCard 
                  title="Current Month Profit"
                  value={currentMonthProfit.overallProfit}
                  subtitle={`A1: ${activeTab === 'INR' ? '₹' : 'AED '}${currentMonthProfit.a1TotalNetProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Transactions: ${activeTab === 'INR' ? '₹' : 'AED '}${currentMonthProfit.transactionsTotalNetProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Expense: ${activeTab === 'INR' ? '₹' : 'AED '}${currentMonthProfit.expenseTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period={getPeriodLabel('currentMonth')}
                  currency={activeTab}
                />
                <MetricCard 
                  title="Last Month Profit"
                  value={lastMonthProfit.overallProfit}
                  subtitle={`A1: ${activeTab === 'INR' ? '₹' : 'AED '}${lastMonthProfit.a1TotalNetProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Transactions: ${activeTab === 'INR' ? '₹' : 'AED '}${lastMonthProfit.transactionsTotalNetProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Expense: ${activeTab === 'INR' ? '₹' : 'AED '}${lastMonthProfit.expenseTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period={getPeriodLabel('lastMonth')}
                  currency={activeTab}
                />
                <MetricCard 
                  title="Last Year Profit"
                  value={lastYearProfit.overallProfit}
                  subtitle={`A1: ${activeTab === 'INR' ? '₹' : 'AED '}${lastYearProfit.a1TotalNetProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Transactions: ${activeTab === 'INR' ? '₹' : 'AED '}${lastYearProfit.transactionsTotalNetProfit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Expense: ${activeTab === 'INR' ? '₹' : 'AED '}${lastYearProfit.expenseTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period={getPeriodLabel('lastYear')}
                  currency={activeTab}
                />
              </div>
            </div>

            {/* Expense Overview */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Spending Overview ({activeTab})</h2>
              <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <MetricCard 
                  title="Current Month Spending"
                  value={currentMonthProfit.expenseTotalAmount}
                  period={getPeriodLabel('currentMonth')}
                  currency={activeTab}
                />
                <MetricCard 
                  title="Last Month Spending"
                  value={lastMonthProfit.expenseTotalAmount}
                  period={getPeriodLabel('lastMonth')}
                  currency={activeTab}
                />
                <MetricCard 
                  title="Last Year Spending"
                  value={lastYearProfit.expenseTotalAmount}
                  period={getPeriodLabel('lastYear')}
                  currency={activeTab}
                />
              </div>
            </div>

            {/* Charity Stats (1% of Overall Profit) */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Charity Allocation ({activeTab}) - 1% of Profit</h2>
              <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <MetricCard 
                  title="Current Month Charity"
                  value={currentMonthCharity.charityTarget}
                  subtitle={`Target: ${activeTab === 'INR' ? '₹' : 'AED '}${currentMonthCharity.charityTarget.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Spent: ${activeTab === 'INR' ? '₹' : 'AED '}${currentMonthCharity.charitySpent.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | ${currentMonthCharity.charityRemaining > 0 ? `Remaining: ${activeTab === 'INR' ? '₹' : 'AED '}${currentMonthCharity.charityRemaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `Surplus: ${activeTab === 'INR' ? '₹' : 'AED '}${currentMonthCharity.charitySurplus.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}`}
                  period={getPeriodLabel('currentMonth')}
                  currency={activeTab}
                />
                <MetricCard 
                  title="Last Month Charity"
                  value={lastMonthCharity.charityTarget}
                  subtitle={`Target: ${activeTab === 'INR' ? '₹' : 'AED '}${lastMonthCharity.charityTarget.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Spent: ${activeTab === 'INR' ? '₹' : 'AED '}${lastMonthCharity.charitySpent.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | ${lastMonthCharity.charityRemaining > 0 ? `Remaining: ${activeTab === 'INR' ? '₹' : 'AED '}${lastMonthCharity.charityRemaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `Surplus: ${activeTab === 'INR' ? '₹' : 'AED '}${lastMonthCharity.charitySurplus.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}`}
                  period={getPeriodLabel('lastMonth')}
                  currency={activeTab}
                />
                <MetricCard 
                  title="Last Year Charity"
                  value={lastYearCharity.charityTarget}
                  subtitle={`Target: ${activeTab === 'INR' ? '₹' : 'AED '}${lastYearCharity.charityTarget.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Spent: ${activeTab === 'INR' ? '₹' : 'AED '}${lastYearCharity.charitySpent.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | ${lastYearCharity.charityRemaining > 0 ? `Remaining: ${activeTab === 'INR' ? '₹' : 'AED '}${lastYearCharity.charityRemaining.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `Surplus: ${activeTab === 'INR' ? '₹' : 'AED '}${lastYearCharity.charitySurplus.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}`}
                  period={getPeriodLabel('lastYear')}
                  currency={activeTab}
                />
              </div>
            </div>

            {/* Bar Chart: Monthly vs Yearly Comparison */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Monthly vs Yearly Trend ({activeTab})</h2>
              <div className="dashboard-card">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={[
                    {
                      period: 'Monthly',
                      expense: lastMonthProfit.expenseTotalAmount,
                      overallProfit: lastMonthProfit.overallProfit
                    },
                    {
                      period: 'Yearly',
                      expense: lastYearProfit.expenseTotalAmount,
                      overallProfit: lastYearProfit.overallProfit
                    }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => `${activeTab === 'INR' ? '₹' : 'AED '}${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    />
                    <Legend />
                    <Bar dataKey="expense" fill="#ef4444" name={`Expense (${activeTab})`} />
                    <Bar dataKey="overallProfit" fill="#10b981" name={`Overall Profit (${activeTab})`} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {/* Source Tab Content */}
        {activeTab === 'Source' && (
          <>
            {/* Source Statistics Overview */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Source Performance Statistics</h2>
              <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <MetricCard 
                  title="Total Sources"
                  value={allTimeSourceStats.totalSources}
                  period="All Time"
                  currency="INR"
                  isCount={true}
          />
          <MetricCard 
                  title="Total Amount"
                  value={allTimeSourceStats.totalAmount}
                  period="All Time"
                  currency="INR"
                />
                <MetricCard 
                  title="Current Year Total"
                  value={currentYearSourceStats.currentYearTotal}
                  period={`${getCurrentDateInfo().year}`}
                  currency="INR"
                  trend={currentYearSourceStats.yoyGrowth}
                />
                <MetricCard 
                  title="Previous Year Total"
                  value={currentYearSourceStats.previousYearTotal}
                  period={`${getCurrentDateInfo().year - 1}`}
                  currency="INR"
                />
                <MetricCard 
                  title="Year-over-Year Growth"
                  value={currentYearSourceStats.yoyGrowth}
                  period="YoY"
                  currency="INR"
                  trend={currentYearSourceStats.yoyGrowth}
                />
        </div>
      </div>

            {/* Source Performance Lists */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Source Performance Rankings</h2>
        <div className="metric-cards-grid">
                <ListCard 
                  title="All Time Rankings" 
                  items={allTimeSourcePerformance}
                  period="All Time"
                  valueKey="totalAmount"
                />
                <ListCard 
                  title="Previous Year Rankings" 
                  items={previousYearSourcePerformance}
                  period={`${getCurrentDateInfo().year - 1} (Previous Year)`}
                  valueKey="totalAmount"
                />
                <ListCard 
                  title="Current Year Rankings" 
                  items={currentYearSourcePerformance}
                  period={`${getCurrentDateInfo().year} (Current Year)`}
                  valueKey="totalAmount"
                />
                <ListCard 
                  title="Previous Month Rankings" 
                  items={previousMonthSourcePerformance}
                  period={getPeriodLabel('lastMonth')}
                  valueKey="totalAmount"
                />
                <ListCard 
                  title="Current Month Rankings" 
                  items={currentMonthSourcePerformance}
                  period={getPeriodLabel('currentMonth')}
                  valueKey="totalAmount"
                />
          </div>
          </div>
          </>
        )}

        {/* Client Tab Content */}
        {activeTab === 'Client' && (
          <>
            {/* Client Sub-tabs */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
              <div className="currency-tabs">
                <button
                  className={`currency-tab ${activeClientSubTab === 'profit' ? 'active' : ''}`}
                  onClick={() => setActiveClientSubTab('profit')}
                >
                  Client by Profit
                </button>
                <button
                  className={`currency-tab ${activeClientSubTab === 'amount' ? 'active' : ''}`}
                  onClick={() => setActiveClientSubTab('amount')}
                >
                  Client by Amount
                </button>
                </div>
          </div>

            {/* Client Statistics Overview */}
            {activeClientSubTab === 'profit' && (
              <div style={{ marginBottom: '48px' }}>
                <h2 className="section-title">Client Performance Statistics (By Profit)</h2>
                <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                  <MetricCard 
                    title="Total Clients"
                    value={allTimeClientStatsProfit.totalClients}
                    period="All Time"
                    currency="INR"
                    isCount={true}
                  />
                  <MetricCard 
                    title="Total Net Profit"
                    value={allTimeClientStatsProfit.totalValue}
                    period="All Time"
                    currency="INR"
                  />
                  <MetricCard 
                    title="Current Year Total"
                    value={currentYearClientStatsProfit.currentYearTotal}
                    period={`${getCurrentDateInfo().year}`}
                    currency="INR"
                    trend={currentYearClientStatsProfit.yoyGrowth}
                  />
                  <MetricCard 
                    title="Previous Year Total"
                    value={currentYearClientStatsProfit.previousYearTotal}
                    period={`${getCurrentDateInfo().year - 1}`}
                    currency="INR"
                  />
                  <MetricCard 
                    title="Year-over-Year Growth"
                    value={currentYearClientStatsProfit.yoyGrowth}
                    period="YoY"
                    currency="INR"
                    trend={currentYearClientStatsProfit.yoyGrowth}
                  />
        </div>
      </div>
            )}

            {activeClientSubTab === 'amount' && (
              <div style={{ marginBottom: '48px' }}>
                <h2 className="section-title">Client Performance Statistics (By Amount)</h2>
                <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                  <MetricCard 
                    title="Total Clients"
                    value={allTimeClientStatsAmount.totalClients}
                    period="All Time"
                    currency="INR"
                    isCount={true}
                  />
                  <MetricCard 
                    title="Total Amount"
                    value={allTimeClientStatsAmount.totalValue}
                    period="All Time"
                    currency="INR"
                  />
                  <MetricCard 
                    title="Current Year Total"
                    value={currentYearClientStatsAmount.currentYearTotal}
                    period={`${getCurrentDateInfo().year}`}
                    currency="INR"
                    trend={currentYearClientStatsAmount.yoyGrowth}
                  />
                  <MetricCard 
                    title="Previous Year Total"
                    value={currentYearClientStatsAmount.previousYearTotal}
                    period={`${getCurrentDateInfo().year - 1}`}
                    currency="INR"
                  />
                  <MetricCard 
                    title="Year-over-Year Growth"
                    value={currentYearClientStatsAmount.yoyGrowth}
                    period="YoY"
                    currency="INR"
                    trend={currentYearClientStatsAmount.yoyGrowth}
                  />
          </div>
          </div>
            )}

            {/* Client Performance Lists */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Client Performance Rankings ({activeClientSubTab === 'profit' ? 'By Profit' : 'By Amount'})</h2>
              
              {/* Client by Profit */}
              {activeClientSubTab === 'profit' && (
        <div className="metric-cards-grid">
          <ListCard 
                    title="All Time Rankings" 
                    items={allTimeClientByProfit}
            period="All Time"
                    valueKey="totalNetProfit"
          />
          <ListCard 
                    title="Previous Year Rankings" 
                    items={previousYearClientByProfit}
                    period={`${getCurrentDateInfo().year - 1} (Previous Year)`}
                    valueKey="totalNetProfit"
          />
          <ListCard 
                    title="Current Year Rankings" 
                    items={currentYearClientByProfit}
                    period={`${getCurrentDateInfo().year} (Current Year)`}
                    valueKey="totalNetProfit"
                  />
                  <ListCard 
                    title="Previous Month Rankings" 
                    items={previousMonthClientByProfit}
                    period={getPeriodLabel('lastMonth')}
                    valueKey="totalNetProfit"
          />
          <ListCard 
                    title="Current Month Rankings" 
                    items={currentMonthClientByProfit}
                    period={getPeriodLabel('currentMonth')}
                    valueKey="totalNetProfit"
          />
                </div>
              )}

              {/* Client by Amount */}
              {activeClientSubTab === 'amount' && (
        <div className="metric-cards-grid">
          <ListCard 
                    title="All Time Rankings" 
                    items={allTimeClientByAmount}
                    period="All Time"
                    valueKey="totalAmount"
          />
          <ListCard 
                    title="Previous Year Rankings" 
                    items={previousYearClientByAmount}
                    period={`${getCurrentDateInfo().year - 1} (Previous Year)`}
                    valueKey="totalAmount"
          />
          <ListCard 
                    title="Current Year Rankings" 
                    items={currentYearClientByAmount}
                    period={`${getCurrentDateInfo().year} (Current Year)`}
                    valueKey="totalAmount"
                  />
                  <ListCard 
                    title="Previous Month Rankings" 
                    items={previousMonthClientByAmount}
                    period={getPeriodLabel('lastMonth')}
                    valueKey="totalAmount"
          />
          <ListCard 
                    title="Current Month Rankings" 
                    items={currentMonthClientByAmount}
                    period={getPeriodLabel('currentMonth')}
                    valueKey="totalAmount"
          />
          </div>
              )}
      </div>
          </>
        )}

        {/* Money Tracker Tab Content */}
        {activeTab === 'Money Tracker' && (
          <>
            {/* Overview Statistics */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Money Tracker Overview</h2>
              <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <MetricCard 
                  title="Total Received"
                  value={moneyTrackerStats.combined.totalReceived}
                  subtitle={`Money In: ₹${moneyTrackerStats.moneyIn.totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Money Out: ₹${moneyTrackerStats.moneyOut.totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period="All Time"
                  currency="INR"
                />
                <MetricCard 
                  title="Total Returnable"
                  value={moneyTrackerStats.combined.totalReturnable}
                  subtitle={`Money In: ₹${moneyTrackerStats.moneyIn.totalReturnable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Money Out: ₹${moneyTrackerStats.moneyOut.totalReturnable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period="All Time"
                  currency="INR"
                />
                <MetricCard 
                  title="Outstanding Balance"
                  value={moneyTrackerStats.combined.outstanding}
                  subtitle={`Money In: ₹${moneyTrackerStats.moneyIn.outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Money Out: ₹${moneyTrackerStats.moneyOut.outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period="All Time"
                  currency="INR"
                />
                <MetricCard 
                  title="Total Collected"
                  value={moneyTrackerStats.combined.totalCollected}
                  subtitle={`Money In: ₹${moneyTrackerStats.moneyIn.totalCollected.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Money Out: ₹${moneyTrackerStats.moneyOut.totalCollected.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period="All Time"
                  currency="INR"
                />
                <MetricCard 
                  title="Net Cash Flow"
                  value={moneyTrackerStats.combined.netFlow}
                  subtitle={moneyTrackerStats.combined.netFlow >= 0 ? "Positive (More In than Out)" : "Negative (More Out than In)"}
                  period="All Time"
                  currency="INR"
                />
                <MetricCard 
                  title="Total Charges"
                  value={moneyTrackerStats.combined.totalCharges}
                  subtitle={`Money In: ₹${moneyTrackerStats.moneyIn.totalCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Money Out: ₹${moneyTrackerStats.moneyOut.totalCharges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  period="All Time"
                  currency="INR"
                />
              </div>
            </div>

            {/* Money In vs Money Out Breakdown */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Money In vs Money Out</h2>
              <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <div className="dashboard-card">
                  <h3 style={{ marginBottom: '16px', fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>Money In Statistics</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Total Received</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>₹{moneyTrackerStats.moneyIn.totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Total Returnable</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>₹{moneyTrackerStats.moneyIn.totalReturnable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Collected</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#10b981' }}>₹{moneyTrackerStats.moneyIn.totalCollected.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Outstanding</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#ef4444' }}>₹{moneyTrackerStats.moneyIn.outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '12px' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '8px' }}>Status Breakdown</div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '0.875rem' }}>
                      <div>
                        <span style={{ color: '#10b981', fontWeight: '600' }}>Cleared: {moneyTrackerStats.moneyIn.cleared}</span>
                      </div>
                      <div>
                        <span style={{ color: '#f59e0b', fontWeight: '600' }}>Pending: {moneyTrackerStats.moneyIn.pending}</span>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280', fontWeight: '600' }}>Not Started: {moneyTrackerStats.moneyIn.notStarted}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="dashboard-card">
                  <h3 style={{ marginBottom: '16px', fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>Money Out Statistics</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Total Received</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>₹{moneyTrackerStats.moneyOut.totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Total Returnable</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>₹{moneyTrackerStats.moneyOut.totalReturnable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Collected</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#10b981' }}>₹{moneyTrackerStats.moneyOut.totalCollected.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Outstanding</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#ef4444' }}>₹{moneyTrackerStats.moneyOut.outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '12px' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '8px' }}>Status Breakdown</div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '0.875rem' }}>
                      <div>
                        <span style={{ color: '#10b981', fontWeight: '600' }}>Cleared: {moneyTrackerStats.moneyOut.cleared}</span>
                      </div>
                      <div>
                        <span style={{ color: '#f59e0b', fontWeight: '600' }}>Pending: {moneyTrackerStats.moneyOut.pending}</span>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280', fontWeight: '600' }}>Not Started: {moneyTrackerStats.moneyOut.notStarted}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Trends Chart */}
            {monthlyTrends.length > 0 && (
              <div style={{ marginBottom: '48px' }}>
                <h2 className="section-title">Monthly Cash Flow Trends</h2>
                <div className="dashboard-card">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={monthlyTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      />
                      <Legend />
                      <Bar dataKey="moneyIn" fill="#10b981" name="Money In" />
                      <Bar dataKey="moneyOut" fill="#ef4444" name="Money Out" />
                      <Bar dataKey="netFlow" fill="#3b82f6" name="Net Flow" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Top Clients - Money In */}
            {getTopMoneyInClients().length > 0 && (
              <div style={{ marginBottom: '48px' }}>
                <h2 className="section-title">Top Clients - Money In</h2>
                <div className="dashboard-card">
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Rank</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Name</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Code</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Total Received</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Total Returnable</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Outstanding</th>
                          <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Entries</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getTopMoneyInClients().map((client, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '12px', fontSize: '0.875rem', color: '#6b7280' }}>{index + 1}</td>
                            <td style={{ padding: '12px', fontSize: '0.875rem', color: '#111827', fontWeight: '500' }}>{client.name}</td>
                            <td style={{ padding: '12px', fontSize: '0.875rem', color: '#6b7280' }}>{client.code}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', color: '#111827', fontWeight: '500' }}>₹{client.totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', color: '#111827', fontWeight: '500' }}>₹{client.totalReturnable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', color: client.outstanding > 0 ? '#ef4444' : '#10b981', fontWeight: '500' }}>₹{client.outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>{client.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Top Clients - Money Out */}
            {getTopMoneyOutClients().length > 0 && (
              <div style={{ marginBottom: '48px' }}>
                <h2 className="section-title">Top Clients - Money Out</h2>
                <div className="dashboard-card">
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Rank</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Name</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Code</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Total Received</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Total Returnable</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Outstanding</th>
                          <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Entries</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getTopMoneyOutClients().map((client, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '12px', fontSize: '0.875rem', color: '#6b7280' }}>{index + 1}</td>
                            <td style={{ padding: '12px', fontSize: '0.875rem', color: '#111827', fontWeight: '500' }}>{client.name}</td>
                            <td style={{ padding: '12px', fontSize: '0.875rem', color: '#6b7280' }}>{client.code}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', color: '#111827', fontWeight: '500' }}>₹{client.totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', color: '#111827', fontWeight: '500' }}>₹{client.totalReturnable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '0.875rem', color: client.outstanding > 0 ? '#ef4444' : '#10b981', fontWeight: '500' }}>₹{client.outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>{client.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Status Distribution Chart */}
            <div style={{ marginBottom: '48px' }}>
              <h2 className="section-title">Status Distribution</h2>
              <div className="metric-cards-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <div className="dashboard-card">
                  <h3 style={{ marginBottom: '16px', fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>Money In Status</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      { name: 'Cleared', value: moneyTrackerStats.moneyIn.cleared, fill: '#10b981' },
                      { name: 'Pending', value: moneyTrackerStats.moneyIn.pending, fill: '#f59e0b' },
                      { name: 'Not Started', value: moneyTrackerStats.moneyIn.notStarted, fill: '#6b7280' }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="dashboard-card">
                  <h3 style={{ marginBottom: '16px', fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>Money Out Status</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      { name: 'Cleared', value: moneyTrackerStats.moneyOut.cleared, fill: '#10b981' },
                      { name: 'Pending', value: moneyTrackerStats.moneyOut.pending, fill: '#f59e0b' },
                      { name: 'Not Started', value: moneyTrackerStats.moneyOut.notStarted, fill: '#6b7280' }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </>
        )}
        </div>

    </div>
  );
};

export default Dashboard;